// hooks/useNearMissSocketAutosave.js
// WebSocket-based autosave hook for Near Miss form

import { useState, useEffect, useCallback, useRef } from "react";
import {
  initializeNearMissSocket,
  joinNearMiss,
  leaveNearMiss,
  emitNearMissPatch,
  onNearMissConnectionChange,
} from "../services/nearMissSocketService";
import {
  queuePatch,
  flushQueue,
  getPendingCount,
} from "../services/nearMissPatchQueue";

export default function useNearMissSocketAutosave(nearMissId, autosaveErrorsActions = null) {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, saved, queued, error
  const [pendingCount, setPendingCount] = useState(0);
  const joinedRef = useRef(false);

  // Initialize socket and join room
  useEffect(() => {
    if (!nearMissId) return;

    initializeNearMissSocket();

    const handleConnectionChange = async (connected) => {
      setConnectionStatus(connected ? "connected" : "disconnected");
      if (!connected) {
        // Reset joined flag on disconnect so we re-join on reconnect
        joinedRef.current = false;
      }
      if (connected && nearMissId) {
        try {
          if (!joinedRef.current) {
            await joinNearMiss(nearMissId);
            joinedRef.current = true;
          }
          const flushed = await flushQueue(nearMissId, emitNearMissPatch);
          if (flushed > 0) {
            const remaining = await getPendingCount(nearMissId);
            setPendingCount(remaining);
          }
        } catch (err) {
          console.error("Error during Near Miss socket reconnection:", err);
        }
      }
    };

    onNearMissConnectionChange(handleConnectionChange);

    // Initial join
    joinNearMiss(nearMissId)
      .then(() => {
        joinedRef.current = true;
      })
      .catch((err) => console.error("Failed to join Near Miss room:", err));

    // Update pending count
    getPendingCount(nearMissId)
      .then(setPendingCount)
      .catch((err) => console.error("Failed to get pending patch count:", err));

    return () => {
      if (nearMissId && joinedRef.current) {
        leaveNearMiss(nearMissId).catch((err) =>
          console.warn("Failed to leave Near Miss room:", err)
        );
        joinedRef.current = false;
      }
    };
  }, [nearMissId]);

  // Emit patch with queue fallback
  // Returns: { success: boolean, error?: string, data?: object }
  const emitPatch = useCallback(
    async (patch) => {
      if (!nearMissId) return { success: false, error: "No Near Miss ID" };

      setSaveStatus("saving");

      const result = await emitNearMissPatch(nearMissId, patch);

      if (!result.error && !result.shouldQueue) {
        // Success - clear errors for this patch type
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);

        // Clear field errors on successful save
        if (autosaveErrorsActions) {
          autosaveErrorsActions.clearPatchTypeErrors("near_miss_main");
        }

        return { success: true, data: result.data };
      }

      if (result.shouldQueue) {
        // Network issue (disconnected, timeout, no response) - queue for retry
        try {
          await queuePatch(nearMissId, patch);
          const count = await getPendingCount(nearMissId);
          setPendingCount(count);
        } catch (idbErr) {
          console.warn(
            "Failed to queue Near Miss patch to browser storage. Your changes may not be saved offline. Try refreshing the page if this persists.",
            idbErr?.message || idbErr
          );
        }
        setSaveStatus("queued");
      } else {
        // Server validation error - show to user, DON'T queue
        setSaveStatus("error");
        console.error("Near Miss patch validation error:", result.error);

        // Use autosaveErrorsActions if available to set field-level errors
        if (autosaveErrorsActions && result.data?.error_type) {
          autosaveErrorsActions.handleServerError(result.data);
        }

        // If the error is unauthorized, redirect to login
        if (result.error === "Unauthorized") {
          window.location.href = "/login";
        }
        // Reset to idle after showing error briefly
        setTimeout(() => setSaveStatus("idle"), 3000);
      }

      return { success: false, error: result.error, data: result.data };
    },
    [nearMissId, autosaveErrorsActions]
  );

  return {
    emitPatch,
    connectionStatus,
    saveStatus,
    pendingCount,
  };
}
